import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import { flowStyles } from "../styles/flow-styles";
import { loadUserSettings, saveFocusedEntry } from "../utils/entry-flow";
import { DEFAULT_SETTINGS } from "../types";
import type { UserSettings } from "../types";

/**
 * Focused flow: log a long-acting (background) insulin dose. No GL required.
 * See docs/add-task-flows.md.
 */
@customElement("add-long-page")
export class AddLongPage extends LitElement {
  @state() private dose = "";
  @state() private isSaving = false;
  @state() private showSaved = false;
  @state() private settings: UserSettings = { ...DEFAULT_SETTINGS };

  static styles = [
    flowStyles,
    css`
      .biFrequency-note {
        font-size: 0.8rem;
        color: #6c757d;
        margin-top: 0.25rem;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    void this._load();
  }

  private async _load() {
    this.settings = await loadUserSettings();
  }

  private _handleBack() {
    Router.go("/");
  }

  private async _handleSave() {
    const dose = parseFloat(this.dose);
    if (isNaN(dose) || dose <= 0 || dose > 200) return;

    this.isSaving = true;
    try {
      await saveFocusedEntry({ biBase: dose }, this.settings);
      this.showSaved = true;
      setTimeout(() => Router.go("/"), 500);
    } catch (err) {
      console.error("Failed to save long insulin:", err);
      this.isSaving = false;
    }
  }

  render() {
    const dose = parseFloat(this.dose);
    const canSave = !isNaN(dose) && dose > 0 && !this.isSaving;

    return html`
      <div class="page-container">
        ${this.showSaved
          ? html`
              <wa-callout variant="success" open class="success-banner">
                <wa-icon name="circle-check" slot="icon"></wa-icon>
                Long insulin saved!
              </wa-callout>
            `
          : nothing}

        <div class="page-header">
          <button class="back-btn" @click=${this._handleBack} aria-label="Back">
            <wa-icon name="arrow-left"></wa-icon>
          </button>
          <h1>Long insulin</h1>
        </div>

        <div class="form-section">
          <div class="input-group">
            <div class="input-label">
              <wa-icon name="prescription-bottle-medical"></wa-icon>
              Background dose
            </div>
            <wa-input
              id="dose-input"
              class="flow-input"
              type="number"
              inputmode="decimal"
              min="0"
              max="200"
              step="0.5"
              placeholder="units"
              .value=${this.dose}
              @input=${(e: Event) =>
                (this.dose = (e.target as HTMLInputElement).value)}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter" && canSave) this._handleSave();
              }}
              autofocus
            ></wa-input>
            <div class="unit-hint">units</div>
          </div>

          <p class="help-text">
            Your long-acting (background) insulin. Logging it here keeps your
            diary complete - it does not change any recommendation.
          </p>

          <div class="actions">
            <wa-button
              class="save-btn"
              variant="brand"
              size="large"
              ?disabled=${!canSave}
              ?loading=${this.isSaving}
              @click=${this._handleSave}
            >
              ${this.isSaving ? "Saving..." : "Save long insulin"}
            </wa-button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "add-long-page": AddLongPage;
  }
}
